"use client";

import React, { useMemo } from "react";
import { Line, LineChart } from "recharts";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { ArrowRight, TrendingUp, MapPin } from "lucide-react";
function RealEstatePortalContent() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const getChartData = React.useCallback((id: string) => {
    const numId = parseInt(id.replace(/\D/g, "")) || Math.random() * 1000;
    return Array.from({
      length: 10
    }).map((_, i) => ({
      time: i,
      price: 500000 + numId % 100 * 10000 + Math.sin(i + numId) * 50000 + i * 15000
    }));
  }, []);
  const chartConfig = {
    price: {
      label: "Avg Price",
      color: "var(--primary)"
    }
  };
  if (loading) {
    return <div className="flex gap-6 overflow-hidden py-8">
        {[1, 2, 3, 4].map(i => <Card key={i} data-index={i} className="w-[320px] shrink-0 animate-pulse border-border/50 bg-card/50">
            <div className="h-48 w-full bg-muted/50 rounded-t-xl" />
            <CardHeader>
              <div className="h-6 w-3/4 bg-muted/50 rounded" />
              <div className="h-4 w-1/2 bg-muted/50 rounded mt-2" />
            </CardHeader>
            <CardContent>
              <div className="h-[60px] w-full bg-muted/30 rounded" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-destructive">
        <p>Failed to load market highlights. Please try again later.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>No market highlights available at the moment.</p>
      </div>;
  }
  return <ScrollArea className="w-full whitespace-nowrap pb-6">
      <div className="flex w-max space-x-6 px-1 py-4">
        {posts.map((post, i) => {
        const chartData = getChartData(post.id);
        const isPositiveTrend = chartData[chartData.length - 1].price > chartData[0].price;
        return <Card key={post.id} data-index={i} className="w-[340px] shrink-0 overflow-hidden border-border/40 bg-card/60 backdrop-blur-md transition-all hover:border-primary/50 hover:shadow-[0_0_30px_-10px_rgba(var(--primary),0.3)]">
              {post.featuredImage ? <div className="relative h-48 w-full overflow-hidden">
                  <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute top-3 right-3 rounded-full bg-background/80 px-3 py-1 text-xs font-semibold text-primary backdrop-blur-sm">
                    Just Listed
                  </div>
                </div> : <div className="flex h-48 w-full items-center justify-center bg-muted/20">
                  <MapPin className="h-12 w-12 text-muted-foreground/30" />
                </div>}

              <CardHeader className="pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {post.categories?.[0]?.name || "Market Update"}
                  </span>
                  {isPositiveTrend && <span className="flex items-center text-xs font-medium text-primary">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      +2.4%
                    </span>}
                </div>
                <CardTitle className="font-serif text-2xl tracking-wide text-foreground line-clamp-2 whitespace-normal">
                  {post.title}
                </CardTitle>
                <CardDescription className="line-clamp-2 whitespace-normal text-sm text-muted-foreground/80">
                  {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Explore the latest insights and trends in the Fort Lauderdale luxury real estate market."}
                </CardDescription>
              </CardHeader>

              <CardContent className="pb-4">
                <div className="mt-4 rounded-lg bg-background/40 p-3 border border-border/30">
                  <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>30-Day Trend</span>
                    <span className="font-mono text-foreground">
                      ${(chartData[chartData.length - 1].price / 1000).toFixed(0)}k
                    </span>
                  </div>
                  <ChartContainer config={chartConfig} className="h-[50px] w-full">
                    <LineChart data={chartData} margin={{
                  top: 5,
                  right: 5,
                  left: 5,
                  bottom: 5
                }}>
                      <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel indicator="line" />} />
                      <Line type="monotone" dataKey="price" stroke="var(--color-price)" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ChartContainer>
                </div>
              </CardContent>

              <CardFooter>
                <Link to={post.link || "#"} className="w-full">
                  <Button variant="outline" className="w-full group border-border/50 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                    View Property Details
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>;
      })}
      </div>
      <ScrollBar orientation="horizontal" className="bg-border/20" />
    </ScrollArea>;
}
export default function The33316RealEstatePortal() {
  const wp_query_params = useMemo(() => ({
    post_type: "post",
    per_page: 6
  }), []);
  const wp_query = useMemo(() => new WP_Query(wp_query_params), [wp_query_params]);
  return <section id="the-33316-real-estate-portal" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-background to-slate-900 pointer-events-none" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary backdrop-blur-sm">
              <span className="mr-2 h-2 w-2 rounded-full bg-primary animate-pulse" />
              Live Market Data
            </div>
            <h2 className="font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
              The 33316 <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-300">Portal</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Exclusive market highlights, luxury property listings, and development news for Fort Lauderdale's premier zip code.
            </p>
          </div>
          
          <Link to="/CityGuidePage">
            <Button variant="secondary" className="shrink-0">
              View Full City Guide
            </Button>
          </Link>
        </div>

        <div className="-mx-4 sm:mx-0">
          <PostsProvider wp_query={wp_query}>
            <RealEstatePortalContent />
          </PostsProvider>
        </div>
      </div>
    </section>;
}